import Vue from "vue";

import {
  CHAT_ACTION_DISPLAY_MAPPING,
  CHAT_ACTION_STATUS_MAPPING
} from "@tt-app/constants/exercise";
import {
  ApiExerciseAction,
  ApiHasAudios,
  ChatAllDisplayableObject,
  ChatAllObjects,
  ChatProgressOptions,
  ChatState,
  ChatStatus,
  ConversationProblemsState,
  ExerciseState,
  IsChatHandler
} from "@tt-app/models/exercise";
import { api } from "@tt-app/api";

import { BaseHandler } from "./base";
import { exerciseHandler } from "./exercise";
import { getIfChatDisplayableItem, getIfExerciseAction } from "../api";
import { audioContext } from "@tt-app/utils/audio/audioContext";

/**
 * Default delay between two consecutive message display in milliseconds
 */
const DFLT_MSGS_DELAY = 250;
const DFLT_TROPHY_DELAY = 750;

class ChatHandler extends BaseHandler implements IsChatHandler {
  // --------------------------------------------------------------------------
  //  Initialisation
  // --------------------------------------------------------------------------
  constructor() {
    super();
  }

  // --------------------------------------------------------------------------
  //  Methods
  // --------------------------------------------------------------------------
  /**
   * Initialise a pristine chat state
   */
  getNewState(): ChatState {
    this._debug(`Chat.getNewChatState`);

    return {
      allowUserInteraction: false,
      audioPlay: {
        currentlyPlayedIndex: -1,
        status: "ready"
      },
      config: {
        msgDelay: DFLT_MSGS_DELAY,
        trophyDelay: DFLT_TROPHY_DELAY
      },
      display: {
        mainWindow: "none",
        lastItem: { type: "PLAIN_TEXT", body: ["Pouet"], textAlign: "center" }
      },
      data: undefined,
      messages: {
        toDisplay: [],
        inQueue: []
      },
      nonProblemMessages: [],
      msgIdx: 0,
      status: "Initializing"
    };
  }

  /**
   * Load chat data
   */
  async loadData<V extends Vue>(
    component: V,
    state: ChatState,
    exerciseState: ExerciseState,
    forceReload = true
  ): Promise<ChatState> {
    if (!forceReload && state.data) {
      this._info(`Chat.loadData: data already loaded, skipping...`);
      return state;
    }
    // Fetch data
    const info = await exerciseHandler.getAuthExerciseInfo(component, exerciseState);
    const data = await api.exercise.getChatBasedExercise(info);

    // Extract non-problem data
    let nonProblemMessages: ChatAllObjects[] = [];
    if (data && data.exercise.pages.length) {
      const chatBody = data.exercise.pages[0].body;
      // 27-Jul-2020: Only exclude Chat conversation problems as the
      // order needs a special handling
      nonProblemMessages = chatBody.filter(chatItem => chatItem.type !== "CHAT_PROBLEM");
    }

    // this._debug(`Chat.loadData: Loaded chat ${JSON.stringify(chat)}`);
    this._info(`Chat.loadData: Loaded chat ${data.exercise.title}`);

    return {
      ...state,
      data,
      display: {
        ...state.display,
        mainWindow: "chat"
      },
      messages: {
        toDisplay: [],
        inQueue: []
      },
      msgIdx: 0,
      nonProblemMessages,
      status: "Initializing"
    };
  }

  loadConversationProblemsQueue(
    state: ChatState,
    conversProblems: ConversationProblemsState
  ): ChatState {
    return {
      ...state,
      status: "ProblemDisplaying",
      messages: { ...state.messages, inQueue: conversProblems.display.inQueue }
    };
  }

  moveToNextMessage(options: ChatProgressOptions): void {
    const { state } = options;

    // Data check
    if (!state.data) {
      this._warn("loadNextMessage: No Chat data !!");
      return;
    }

    // Check if queue has something to display
    if (state.messages.inQueue.length) {
      this.handleInQueueMsgs(options);
      return;
    }

    // Move forward with standard messages
    const nextMsgIdx = state.msgIdx + 1;
    if (nextMsgIdx > state.nonProblemMessages.length) {
      this._warn(
        `Next message index ${nextMsgIdx} is out of bounds of messages ${JSON.stringify(
          state.nonProblemMessages
        )}`
      );
      return;
    }

    const nextItem = state.nonProblemMessages[state.msgIdx];

    this._debug(`Chat.loadNextMessage: ${JSON.stringify(nextItem)}`);

    if (nextItem === undefined) {
      console.warn(`undefined!!`, state);
      return;
    }

    // Just playing a message
    const itemAsChatItem = getIfChatDisplayableItem(nextItem);
    if (itemAsChatItem) {
      this.handleChatDisplaybleItem(options, itemAsChatItem, nextMsgIdx);
      return;
    }

    // No additional message, but save the `lastItem` to forward to FooterHandler
    const itemAsAction = getIfExerciseAction(nextItem);
    if (itemAsAction) {
      this.handleExerciseActionItem(options, itemAsAction, nextMsgIdx);
      return;
    }

    // Dictation problem found: save in "last item" and let the appropriate
    // handler do the rest
    if (nextItem.type === "DICTATION_PROBLEM") {
      options.onMsgDisplayEnded({
        ...state,
        display: { ...state.display, lastItem: nextItem },
        msgIdx: nextMsgIdx
      });
      return;
    }

    // Unexpected
    else {
      this._warn("loadNextMessage has nothing to do");
      return;
    }
  }

  async displayNewMessage(options: ChatProgressOptions): Promise<boolean> {
    const { state, audioDecoderPlayer } = options;

    // Extract last displayed message
    const displayedMsgs = state.messages.toDisplay;
    const lastDisplayedMsg = state.display.lastItem;
    const msgAsHasAudio = lastDisplayedMsg as ApiHasAudios;

    // Check if callback is provided
    const onNext = () => {
      const isLastMsg =
        state.status === "Ended" && state.msgIdx === state.nonProblemMessages.length - 1;
      // const isLastMsg = state.status === "Ended";

      if (isLastMsg) {
        return;
      }

      window.setTimeout(async () => {
        // If the queue was the last message of a problem display, next action is
        // to wait for user input
        let status: ChatStatus = state.status;
        if (status === "ProblemDisplaying" && state.messages.inQueue.length === 0) {
          status = "ProblemWaitingForUserAnswer";
        }

        await options.onMsgDisplayEnded({
          ...state,
          audioPlay: { currentlyPlayedIndex: -1, status: "ready" },
          status
        });
      }, state.config.msgDelay);
    };

    if (msgAsHasAudio && msgAsHasAudio.audioUrls && msgAsHasAudio.autoPlay) {
      if (!audioDecoderPlayer) {
        this._warn(`AudioPlayer is undefined to play ${JSON.stringify(msgAsHasAudio)}`);
        return false;
      }

      // Stop currently played audio
      audioDecoderPlayer.stop();

      // In case some actions are required before the audio play
      options.onMsgDisplayStarted({
        ...state,
        audioPlay: {
          currentlyPlayedIndex: displayedMsgs.length - 1,
          status: "playing"
        }
      });

      await audioContext.resume();
      audioDecoderPlayer.play(msgAsHasAudio.audioUrls, { onEnded: onNext });

      return true;
    } else {
      options.onMsgDisplayStarted(state);

      onNext();

      return false;
    }
  }

  private handleInQueueMsgs(options: ChatProgressOptions) {
    const { state } = options;

    const firstQueued = state.messages.inQueue[0];
    const newInQueueMsgs = state.messages.inQueue.slice(1);
    this._debug(`load queue message: ${JSON.stringify(firstQueued)}`);

    const updatedState: ChatState = {
      ...state,
      display: { ...state.display, lastItem: firstQueued },
      messages: {
        toDisplay: [...state.messages.toDisplay, firstQueued],
        inQueue: newInQueueMsgs
      }
    };

    this.displayNewMessage({ ...options, state: updatedState });
  }

  private handleChatDisplaybleItem(
    options: ChatProgressOptions,
    chatItem: ChatAllDisplayableObject,
    nextMsgIdx: number
  ) {
    const { state } = options;

    const nextStatus =
      state.status === "IntroBotPlaying" || state.status === "Initializing"
        ? "IntroBotPlaying"
        : "MsgBotPlaying";

    const updatedState: ChatState = {
      ...state,
      display: {
        ...state.display,
        lastItem: chatItem
      },
      messages: {
        ...state.messages,
        toDisplay: [...state.messages.toDisplay, chatItem]
      },
      msgIdx: nextMsgIdx,
      status: nextStatus
    };

    this.displayNewMessage({ ...options, state: updatedState });
  }

  private handleExerciseActionItem(
    options: ChatProgressOptions,
    actionItem: ApiExerciseAction,
    nextMsgIdx: number
  ) {
    const updatedState: ChatState = {
      ...options.state,
      display: { mainWindow: CHAT_ACTION_DISPLAY_MAPPING[actionItem.type], lastItem: actionItem },
      msgIdx: nextMsgIdx,
      status: CHAT_ACTION_STATUS_MAPPING[actionItem.type]
    };

    // Leave the chat for a while before switching to the trophy view
    if (updatedState.display.mainWindow === "trophy") {
      window.setTimeout(
        () => this.displayNewMessage({ ...options, state: updatedState }),
        updatedState.config.trophyDelay
      );
      return;
    }

    this.displayNewMessage({ ...options, state: updatedState });
  }
}

export const chatHandler = new ChatHandler();
